import { JwtService } from 'api';
import { isDefinitiveAuthRejection } from './authError';
import {
  clearOtpVerified,
  clearStoredAuthTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  isAuthLogoutInProgress,
  setRefreshedAuthTokens,
} from './authStorage';

export type TokenRefreshResult =
  | { kind: 'success'; access: string }
  | { kind: 'cleared' }
  | { kind: 'transient' };

let refreshInFlight: Promise<TokenRefreshResult> | null = null;

const clearExpiredAuthSession = () => {
  clearOtpVerified();
  clearStoredAuthTokens();
};

const executeTokenRefresh = async (): Promise<TokenRefreshResult> => {
  const refresh = getStoredRefreshToken();

  if (!refresh) {
    clearExpiredAuthSession();
    return { kind: 'cleared' };
  }

  try {
    const response = await JwtService.authJwtRefreshCreate({ refresh });

    if (response?.access) {
      // logOutUser clears storage synchronously, so a refresh already in flight would otherwise
      // re-arm an access token after the user signed out. Callers must not treat that as a session.
      if (isAuthLogoutInProgress()) {
        clearExpiredAuthSession();
        return { kind: 'cleared' };
      }

      // Inert until the server enables ROTATE_REFRESH_TOKENS
      setRefreshedAuthTokens({ accessToken: response.access, refreshToken: response.refresh });
      return { kind: 'success', access: response.access };
    }

    // A successful HTTP response without an access token is unusable; treat it as expired.
    clearExpiredAuthSession();
    return { kind: 'cleared' };
  } catch (error) {
    // Only wipe the session when the refresh token is definitively rejected.
    // Network blips and 5xx must leave stored tokens intact so the next retry can recover.
    if (isDefinitiveAuthRejection(error)) {
      const storedRefresh = getStoredRefreshToken();

      // Tabs share storage but not the in-flight promise, so two of them can send the same token.
      // With rotation on, the loser's token is already retired and the server rejects it — but the
      // winner has written a fresh pair, and clearing here would throw that working session away.
      // setRefreshedAuthTokens writes the access token first, so a changed refresh value means the
      // matching access token is already stored.
      if (storedRefresh && storedRefresh !== refresh) {
        const storedAccess = getStoredAccessToken();

        if (storedAccess) {
          return { kind: 'success', access: storedAccess };
        }

        // Caught mid-write: tokens are intact, so let the caller retry rather than wiping.
        return { kind: 'transient' };
      }

      clearExpiredAuthSession();
      return { kind: 'cleared' };
    }

    return { kind: 'transient' };
  }
};

/**
 * Issues at most one /auth/jwt/refresh request at a time.
 * Concurrent callers share the in-flight promise and receive the same result.
 *
 * Returns a discriminated `TokenRefreshResult`:
 *  - `success`   — new access token available
 *  - `cleared`   — session was definitively wiped (401/403 or missing refresh token)
 *  - `transient` — network/5xx error; tokens are intact and a retry is safe
 */
export const singleFlightTokenRefresh = (): Promise<TokenRefreshResult> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = executeTokenRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};
